export type TeamMember = {
  id: string;
  name: string;
  email: string;
  phone: string;
  role: 'superadmin' | 'sales_manager' | 'field_sales' | 'handelsvertreter' | 'caller' | 'ai_agent';
  plzAreas: string[]; // PLZ prefixes they can work in
  isActive: boolean;
  externalDomain?: string; // For Handelsvertreter with @extern.clanda.de
};

export type Team = {
  id: string;
  name: string;
  description: string;
  members: TeamMember[];
};

// Real team data — mirrors public.users table in production DB
export const productionTeamMembers: TeamMember[] = [
  // Superadmin
  {
    id: 'fe6d4c02-c0a2-4f48-8f56-17e4c2d8c1fc',
    name: 'David Fuchs',
    email: 'david.fuchs@extern.clanda.de',
    phone: '',
    role: 'superadmin',
    plzAreas: ['*'],
    isActive: true,
    externalDomain: 'extern.clanda.de',
  },

  // Sales Manager
  {
    id: 'bee33df0-e613-4b7b-98ad-40d6cf9a9638',
    name: 'Benno Gässler',
    email: 'benno.gaessler@clanda.de',
    phone: '+49 179 4383519',
    role: 'sales_manager',
    plzAreas: ['55', '56', '64', '77', '83'],
    isActive: true,
  },

  // Field Sales (VAD)
  {
    id: '0341ece2-2df3-440a-a6c4-f5e757194ada',
    name: 'Steffen Heinecke',
    email: 'steffen.heinecke@clanda.de',
    phone: '+49 1517 3023366',
    role: 'field_sales',
    plzAreas: ['77'],
    isActive: true,
  },
  {
    id: '6c06ed80-d61e-4760-a46f-9e4168ac988c',
    name: 'Sigi Binder',
    email: 'sigi.binder@clanda.de',
    phone: '+49 1520 2658384',
    role: 'field_sales',
    plzAreas: ['83'],
    isActive: true,
  },
  {
    id: '4fb456cd-d610-454a-bb2c-f99146480cd0',
    name: 'Rüdiger Jerg',
    email: 'ruediger.jerg@clanda.de',
    phone: '+49 1520 6400786',
    role: 'field_sales',
    plzAreas: ['76', '55', '56', '64'],
    isActive: true,
  },
  {
    id: '8482ab79-8265-45f6-946a-031d80b3eb41',
    name: 'Jürgen Wolf',
    email: 'juergen.wolf@clanda.de',
    phone: '+49 173 1614653',
    role: 'field_sales',
    plzAreas: ['72'],
    isActive: true,
  },
  {
    id: '9c30da45-623f-4ccd-80d2-4618d105c030',
    name: 'Mark Müller',
    email: 'mark.mueller@clanda.de',
    phone: '',
    role: 'field_sales',
    plzAreas: [],
    isActive: true,
  },

  // Handelsvertreter (HV)
  {
    id: '7bff3b6a-d309-48ea-9f77-133108a9e471',
    name: 'Guido Frankemann',
    email: 'guido.frankemann@extern.clanda.de',
    phone: '+49 170 2043993',
    role: 'handelsvertreter',
    plzAreas: ['48', '49'],
    isActive: true,
    externalDomain: 'extern.clanda.de',
  },
  {
    id: '36ba2687-00a3-43f7-aad3-8897176fd8fe',
    name: 'Markus Schwander',
    email: 'markus.schwander@extern.clanda.de',
    phone: '+49 160 93087159',
    role: 'handelsvertreter',
    plzAreas: ['95'],
    isActive: true,
    externalDomain: 'extern.clanda.de',
  },
  {
    id: 'de36437e-427d-490f-bdc8-03133689e0d9',
    name: 'Michael Löbbecke',
    email: 'michael.loebbecke@extern.clanda.de',
    phone: '+49 170 4696710',
    role: 'handelsvertreter',
    plzAreas: ['42', '44', '45'],
    isActive: true,
    externalDomain: 'extern.clanda.de',
  },
];

// Team structure
export const productionTeams: Team[] = [
  {
    id: 'team-clanda-core',
    name: 'VAD',  // Changed from 'Clanda Core Team'
    description: 'Internal sales team managed by Benno Gässler',
    members: productionTeamMembers.filter(m => 
      m.role === 'sales_manager' || m.role === 'field_sales'
    ),
  },
  {
    id: 'team-handelsvertreter',
    name: 'HV',  // Changed from 'Handelsvertreter'
    description: 'Independent external sales representatives',
    members: productionTeamMembers.filter(m => m.role === 'handelsvertreter'),
  },
];

// Helper functions
export function getTeamMemberById(id: string): TeamMember | undefined {
  return productionTeamMembers.find(member => member.id === id);
}

export function getTeamMembersByRole(role: TeamMember['role']): TeamMember[] {
  return productionTeamMembers.filter(member => member.role === role);
}

export function getTeamMembersByPlzArea(plzPrefix: string): TeamMember[] {
  return productionTeamMembers.filter(member => 
    member.plzAreas.includes('*') || member.plzAreas.includes(plzPrefix)
  );
}

export function getActiveTeamMembers(): TeamMember[] {
  return productionTeamMembers.filter(member => member.isActive);
}

// Role hierarchy for access control
export const roleHierarchy: Record<TeamMember['role'], number> = {
  'superadmin': 4,
  'sales_manager': 3,
  'field_sales': 2,
  'handelsvertreter': 1,
  'caller': 1,
  'ai_agent': 0,
};

export function hasRoleAccess(userRole: TeamMember['role'], requiredRole: TeamMember['role']): boolean {
  return roleHierarchy[userRole] >= roleHierarchy[requiredRole];
}

// Special access rules
export function canAccessHandelsvertreterData(userRole: TeamMember['role']): boolean {
  return userRole === 'superadmin'; // Only David can see HV data
}

export function canAccessPlzArea(userRole: TeamMember['role'], userPlzAreas: string[], targetPlz: string): boolean {
  if (userRole === 'superadmin') return true;
  if (userRole === 'sales_manager') {
    const teamAreas = ['55', '56', '64', '72', '76', '77', '83'];
    return teamAreas.includes(targetPlz);
  }
  // Field sales can only see their assigned areas
  return userPlzAreas.includes(targetPlz);
}
